// src/supabase.js
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = 'https://tpxzotpgkcaioifgzlii.supabase.co'
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRweHpvdHBna2NhaW9pZmd6bGlpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk0ODI4MTIsImV4cCI6MjA4NTA1ODgxMn0.Re0nm2Nu4BRFf73F9kchoZDjY8BpG1N0Qfe7BtzE1tE'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

console.log('Supabase initialized:', supabaseUrl)

// =============================================================================
// セッション JWT を取得（認証済みユーザーが Edge Function を呼ぶ時に使う）
// SDK 管理のセッション（自動リフレッシュされる）を優先、フォールバックで localStorage
// =============================================================================
const getSessionToken = async () => {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || localStorage.getItem('supabase_token');
};

// 認証済みユーザー用の Edge Function 呼び出しヘルパー
const callAuthedFunction = async (functionName, body) => {
  const sessionToken = await getSessionToken();
  if (!sessionToken) {
    throw new Error('ログインが必要です');
  }
  const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${sessionToken}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify(body || {}),
  });
  return response.json();
};

// =============================================================================
// ログイン（認証前なので anon key を使う）
// =============================================================================
export const loginWithEmployeeId = async (employeeId, password) => {
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ employeeId, password })
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.message || 'ログインに失敗しました')
    return data
  } catch (error) {
    console.error('Login error:', error)
    throw error
  }
}

// =============================================================================
// パスワード変更（認証済み）
// =============================================================================
export const changePassword = async (token, newPassword) => {
  try {
    // 認証済みユーザー前提：session token を Authorization に渡す
    const data = await callAuthedFunction('change-password', { token, newPassword })
    if (!data.success) throw new Error(data.message || 'パスワード変更に失敗しました')
    return data
  } catch (error) {
    console.error('Change password error:', error)
    throw error
  }
}

// =============================================================================
// スタッフ管理
// =============================================================================
export const getStaffList = async (organizationId) => {
  if (!organizationId) throw new Error('organizationId is required');
  try {
    const data = await callAuthedFunction('get-staff-list', { organizationId });
    if (!data.success) throw new Error(data.message || 'スタッフ一覧取得に失敗しました');
    return data.staff;
  } catch (error) {
    console.error('getStaffList error:', error);
    throw error;
  }
};

export const addStaff = async (employeeId, lastName, firstName, email, year, role, organizationId) => {
  if (!organizationId) throw new Error('organizationId is required');
  try {
    const data = await callAuthedFunction('add-staff', {
      employeeId, lastName, firstName, email, year, role, organizationId
    });
    if (!data.success) throw new Error(data.message || 'スタッフ追加に失敗しました');
    return data.staff;
  } catch (error) {
    console.error('addStaff error:', error);
    throw error;
  }
};

export const deleteStaff = async (staffId) => {
  try {
    const data = await callAuthedFunction('delete-staff', { staffId });
    if (!data.success) throw new Error(data.message || 'スタッフ削除に失敗しました');
    return true;
  } catch (error) {
    console.error('deleteStaff error:', error);
    throw error;
  }
};

export const updateStaff = async (staffId, employeeId, lastName, firstName, email, year, role) => {
  try {
    const data = await callAuthedFunction('update-staff', {
      staffId, employeeId, lastName, firstName, email, year, role
    });
    if (!data.success) throw new Error(data.message || 'スタッフ更新に失敗しました');
    return data.staff;
  } catch (error) {
    console.error('updateStaff error:', error);
    throw error;
  }
};

// =============================================================================
// スキルデータ
// =============================================================================
export const getSkillData = async (staffId, organizationId) => {
  if (!organizationId) throw new Error('organizationId is required');
  try {
    const data = await callAuthedFunction('get-skill-data', { staffId, organizationId });
    if (!data.success) throw new Error(data.message || 'スキルデータ取得に失敗しました');
    return data.data;
  } catch (error) {
    console.error('getSkillData error:', error);
    throw error;
  }
};

export const saveSkillRecords = async (records, organizationId) => {
  if (!organizationId) throw new Error('organizationId is required');
  try {
    const data = await callAuthedFunction('save-skill-records', { records, organizationId });
    if (!data.success) throw new Error(data.message || 'スキル記録の保存に失敗しました');
    return data;
  } catch (error) {
    console.error('saveSkillRecords error:', error);
    throw error;
  }
};

// =============================================================================
// マスター管理
// =============================================================================
export const manageDepartments = async (action, data = {}) => {
  try {
    const result = await callAuthedFunction('manage-departments', { action, ...data });
    if (!result.success) throw new Error(result.message || '診療科の操作に失敗しました');
    return result.data;
  } catch (error) {
    console.error('manageDepartments error:', error);
    throw error;
  }
};

export const manageProcedures = async (action, data = {}) => {
  try {
    const result = await callAuthedFunction('manage-procedures', { action, ...data });
    if (!result.success) throw new Error(result.message || '術式の操作に失敗しました');
    return result.data;
  } catch (error) {
    console.error('manageProcedures error:', error);
    throw error;
  }
};

export const manageShiftPatterns = async (action, data = {}) => {
  try {
    const result = await callAuthedFunction('manage-shift-patterns', { action, ...data });
    if (!result.success) throw new Error(result.message || 'シフトパターンの操作に失敗しました');
    return result.data;
  } catch (error) {
    console.error('manageShiftPatterns error:', error);
    throw error;
  }
};

export const manageSkillGroups = async (action, data = {}) => {
  try {
    const result = await callAuthedFunction('manage-skill-groups', { action, ...data });
    if (!result.success) throw new Error(result.message || 'スキルグループの操作に失敗しました');
    return result.data;
  } catch (error) {
    console.error('manageSkillGroups error:', error);
    throw error;
  }
};
